/**
 * FreeFormFill.jsx — Bölüm 25-D
 * Esnek form doldurma ekranı.
 * Route: /FdsSaha/FreeForm/:templateId?fuarId=<id>
 *
 * Akış:
 *  1. Template tree + metadata yükle (getTemplate + getTemplateTree)
 *  2. Bağlam kartı: fuarId seçimi + hedef varlık (STAND/SALON/FAIR/PERSON)
 *  3. Sorular: kategori → soru → cevap alanı
 *  4. Gönder → submitForm → başarı ekranı (PHOTO upload 25-E)
 */

import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Container, Card, CardBody, Badge, Button, Input,
  Alert, Spinner, Label,
} from "reactstrap";
import {
  CheckCircle2, Camera, Send, ChevronLeft, Search, FolderOpen,
} from "lucide-react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import {
  getTemplate, getTemplateTree, submitForm,
  flatFreeFormNodes, uploadFreeFormPhoto,
} from "../../services/freeFormService";
import { getTargets } from "../../services/sahaInspectionService";
import { fetchFuarList } from "../../slices/fuarSlice";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

// ─── Yardımcı: Nested node ağacını düzleştir + logla ─────────────────────────
// flatFreeFormNodes (freeFormService'ten) zaten çekirdeği yapıyor.
// Bu wrapper sadece debug log için kullanılıyor.
function flattenWithLog(tree) {
  const result = flatFreeFormNodes(tree);
  console.log('[FreeFormFill] flattenNodes → toplam node:', result.length,
    '| kategoriler:', result.filter(n => n.layerOrder === 0).length,
    '| sorular:', result.filter(n => n.layerOrder === 1).length);
  return result;
}

// ─── Sabitler ────────────────────────────────────────────────────────────────

const ENTITY_LABELS = { get FAIR() { return i18n.t("portal:esnekForm.fuar"); }, get SALON() { return i18n.t("portal:esnekForm.salon"); }, get STAND() { return i18n.t("portal:esnekForm.stant"); }, get PERSON() { return i18n.t("portal:esnekForm.kisi"); } };
const ENTITY_COLORS = { FAIR: "primary", SALON: "info", STAND: "success", PERSON: "warning" };

// ─── Yardımcı: Cevap Alanı ───────────────────────────────────────────────────

function AnswerField({ question, value, onChange }) {
  const { t } = useTranslation("portal");
  const { answerType, choices = [] } = question;

  if (answerType === "TEXT") {
    return (
      <Input
        type="textarea"
        rows={2}
        bsSize="sm"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t("esnekForm.cevabiniziYazin")}
      />
    );
  }

  if (answerType === "NUMBER") {
    return (
      <Input
        type="number"
        bsSize="sm"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t("esnekForm.sayisalDeger")}
      />
    );
  }

  if (answerType === "SINGLE_CHOICE") {
    return (
      <div className="d-flex flex-wrap gap-2">
        {choices.map((choice, i) => (
          <button
            key={i}
            type="button"
            className={`btn btn-sm ${value === choice ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => onChange(value === choice ? null : choice)}
          >
            {choice}
          </button>
        ))}
        {choices.length === 0 && (
          <span className="text-muted small">{t("esnekForm.secenekTanimlanmamis")}</span>
        )}
      </div>
    );
  }

  if (answerType === "MULTI_CHOICE") {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="d-flex flex-wrap gap-2">
        {choices.map((choice, i) => {
          const isSelected = selected.includes(choice);
          return (
            <button
              key={i}
              type="button"
              className={`btn btn-sm ${isSelected ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() =>
                isSelected
                  ? onChange(selected.filter((c) => c !== choice))
                  : onChange([...selected, choice])
              }
            >
              {choice}
            </button>
          );
        })}
        {choices.length === 0 && (
          <span className="text-muted small">{t("esnekForm.secenekTanimlanmamis")}</span>
        )}
      </div>
    );
  }

  if (answerType === "PHOTO") {
    // PHOTO tipli node şablonda varsa — görmezden gel, inline buton ile halloldu
    return null;
  }

  return <span className="text-muted small">{t("esnekForm.bilinmeyenCevapTuru")}{" "}{answerType}</span>;
}

// ─── Stant Seçici ─────────────────────────────────────────────────────────────

function StandSelector({ fuarId, value, onChange }) {
  const { t } = useTranslation("portal");
  const [search,  setSearch]  = useState("");
  const [stands,  setStands]  = useState([]);
  const [loading, setLoading] = useState(false);

  // getTargets('stand') localStorage'daki activeFairId'yi kullanır.
  // fuarId prop'u geldiğinde activeFairId'yi geçici olarak set ederek yükle.
  const load = useCallback(async () => {
    if (!fuarId) return;
    setLoading(true);
    // activeFairId'yi geçici olarak override et (getTargets okur)
    const prev = localStorage.getItem('activeFairId');
    localStorage.setItem('activeFairId', fuarId);
    try {
      const list = await getTargets('stand');
      // client-side arama filtresi
      const filtered = search.trim()
        ? list.filter((s) =>
            s.name?.toLowerCase().includes(search.toLowerCase()) ||
            s.firma?.toLowerCase().includes(search.toLowerCase())
          )
        : list;
      setStands(filtered);
    } catch {
      setStands([]);
    } finally {
      // önceki değeri geri yükle
      if (prev !== null) localStorage.setItem('activeFairId', prev);
      else localStorage.removeItem('activeFairId');
      setLoading(false);
    }
  }, [fuarId, search]);

  useEffect(() => {
    async function run() { await load(); }
    run();
  }, [load]);

  return (
    <div>
      <div className="input-group input-group-sm mb-2">
        <span className="input-group-text bg-white">
          <Search size={13} />
        </span>
        <Input
          bsSize="sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("esnekForm.stantAdiVeyaFirmaAra")}
        />
      </div>
      {loading && <Spinner size="sm" color="primary" className="d-block mx-auto my-2" />}
      {!loading && stands.length === 0 && (
        <p className="text-muted small text-center py-2">{t("esnekForm.standBulunamadi")}</p>
      )}
      <div style={{ maxHeight: 220, overflowY: "auto" }}>
        {stands.map((s,idx) => (
          <button
            key={`$<span>{s.id}_</span>${idx}`}
            type="button"
            className={`w-100 text-start btn btn-sm mb-1 ${
              value?.id === s.id ? "btn-primary" : "btn-outline-secondary"
            }`}
            onClick={() => onChange({ id: s.id, label: s.name })}
          >
            <strong>{s.name}</strong>
            {s.firma && (
              <span className="ms-2 small opacity-75">{s.firma}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Ana Bileşen ─────────────────────────────────────────────────────────────

export default function FreeFormFill() {
  const { t } = useTranslation("portal");
  const { templateId }    = useParams();
  const [searchParams]    = useSearchParams();
  const navigate          = useNavigate();
  const dispatch          = useDispatch();

  // Redux: fuar listesi
  const fuarList = useSelector((s) => s.fuar?.list || []);

  // ── Template state ────────────────────────────────────────────────────────
  const [template,        setTemplate]        = useState(null);
  const [nodes,           setNodes]           = useState([]);
  const [loadingTemplate, setLoadingTemplate] = useState(true);
  const [templateError,   setTemplateError]   = useState(null);

  // ── Bağlam state ─────────────────────────────────────────────────────────
  const [fuarId, setFuarId] = useState(searchParams.get("fuarId") || "");
  const [target, setTarget] = useState(null); // { id, label }

  // ── Cevaplar ──────────────────────────────────────────────────────────────
  const [answers,    setAnswers]    = useState({});
  const [photoFiles, setPhotoFiles] = useState({}); // { [photoQId]: File } — inline seçilen fotoğraflar

  // ── Submit state ─────────────────────────────────────────────────────────
  const [submitting,   setSubmitting]   = useState(false);
  const [submitError,  setSubmitError]  = useState(null);
  const [submitted,    setSubmitted]    = useState(null);

  // ── Başlangıç yüklemeleri ─────────────────────────────────────────────────
  useEffect(() => {
    document.title = i18n.t("portal:esnekForm.esnekFormDoldurFds");
    if (!fuarList.length) {
      dispatch(fetchFuarList({ page: 0, size: 100 }));
    }
  }, [dispatch, fuarList.length]);

  useEffect(() => {
    if (!templateId) return;
    async function load() {
      setLoadingTemplate(true);
      setTemplateError(null);
      try {
        const [meta, tree] = await Promise.all([
          getTemplate(templateId),
          getTemplateTree(templateId),
        ]);
        console.log('[FreeFormFill] getTemplate response:', meta);
        console.log('[FreeFormFill] getTemplateTree response:', tree);
        setTemplate(meta);
        // Backend "rootNodes" key kullanıyor → flatFreeFormNodes ile düzleştir
        const rawNodes = tree?.rootNodes || tree?.nodes || [];
        console.log('[FreeFormFill] rawNodes (before flatten):', rawNodes);
        setNodes(flattenWithLog(tree));
      } catch (err) {
        setTemplateError(
          err?.response?.data?.message || i18n.t("portal:esnekForm.sablonYuklenemediLutfenTekrarDeneyin")
        );
      } finally {
        setLoadingTemplate(false);
      }
    }
    load();
  }, [templateId]);

  // ── Katman yardımcıları (çok katmanlı destek) ────────────────────────────
  const sortedLayers = [...(template?.layers || [])].sort((a, b) => Number(a.order) - Number(b.order));
  const rootLayerOrder     = Number(sortedLayers[0]?.order ?? 0);
  const questionLayerOrder = Number(sortedLayers.find(l => l.type === 'QUESTION')?.order ?? sortedLayers[sortedLayers.length - 1]?.order ?? 1);

  const rootCategories = nodes.filter(n => Number(n.layerOrder) === rootLayerOrder);
  const allQuestions   = nodes.filter(n => Number(n.layerOrder) === questionLayerOrder);

  function getChildNodes(parentId) {
    const pidStr = String(parentId);
    return nodes.filter(n => String(n.parentId) === pidStr);
  }

  function getLayerType(layerOrder) {
    const l = sortedLayers.find(l => Number(l.order) === Number(layerOrder));
    return l?.type || 'QUESTION';
  }

  // Sorular listesini inline fotoğraf butonu ile render eder
  // PHOTO tipli node'lar şablonda bazen ayrı soru olarak tanımlanır; bunları atla.
  // Her soru için (PHOTO hariç) "Fotoğraf Ekle" butonu otomatik gösterilir.
  function renderQuestions(questions) {
    const visibleQuestions = questions.filter(q => q.answerType !== 'PHOTO');
    return visibleQuestions.map((q, qi) => {
      const qId = q._id || q.id;
      const selectedPhotoFile = photoFiles[qId];
      return (
        <div key={qId} className={`p-3 ${qi < visibleQuestions.length - 1 ? "border-bottom" : ""}`}>
          <div className="mb-2 d-flex align-items-start gap-1 flex-wrap">
            <span className="small text-muted fw-semibold me-1">{qi + 1}.</span>
            <span className="small fw-semibold flex-grow-1">{q.name}</span>
            {q.required && <Badge color="danger" pill style={{ fontSize: "0.6rem" }}>{t("esnekForm.zorunlu")}</Badge>}
            {/* Fotoğraf Ekle butonu — her soru için */}
            <div className="ms-auto">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                id={`photo-input-${qId}`}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) setPhotoFiles((prev) => ({ ...prev, [qId]: file }));
                }}
              />
              <label
                htmlFor={`photo-input-${qId}`}
                className={`btn btn-sm ${selectedPhotoFile ? 'btn-success' : 'btn-outline-secondary'} d-flex align-items-center gap-1`}
                style={{ cursor: 'pointer', fontSize: '0.75rem' }}
              >
                <Camera size={13} />
                {selectedPhotoFile
                  ? (selectedPhotoFile.name.length > 14
                      ? selectedPhotoFile.name.slice(0, 14) + '…'
                      : selectedPhotoFile.name)
                  : t("esnekForm.fotografEkle")}
              </label>
            </div>
          </div>
          <AnswerField question={q} value={answers[qId]} onChange={(val) => setAnswer(qId, val)} />
        </div>
      );
    });
  }

  // Recursive: parentId'nin çocuklarını render eder (CATEGORY veya QUESTION)
  function renderCategoryChildren(parentId) {
    const children = getChildNodes(parentId);
    if (children.length === 0) {
      return <p className="text-muted small text-center py-3 mb-0">{t("esnekForm.buBolumdeSoruYok")}</p>;
    }
    const layerType = getLayerType(Number(children[0].layerOrder));
    if (layerType === 'QUESTION') {
      return renderQuestions(children);
    }
    // Alt kategori bölümleri
    return children.map(subCat => {
      const subCatId = subCat._id || subCat.id;
      return (
        <div key={subCatId} className="border-bottom">
          <div className="d-flex align-items-center gap-2 p-2 border-bottom" style={{ background: '#f0f2f5' }}>
            <FolderOpen size={13} className="text-secondary flex-shrink-0" />
            <span className="fw-semibold small flex-grow-1 text-secondary">{subCat.name}</span>
          </div>
          {renderCategoryChildren(subCatId)}
        </div>
      );
    });
  }

  // ── Cevap setter ─────────────────────────────────────────────────────────
  const setAnswer = (nodeId, val) =>
    setAnswers((prev) => ({ ...prev, [nodeId]: val }));

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!template) return;
    setSubmitError(null);

    const entityType = template.targetEntityType;

    // Fuar + hedef entity çözümleme
    const resolvedFuarId =
      entityType === "FAIR"
        ? target?.id || parseInt(fuarId) || null
        : parseInt(fuarId) || null;

    const resolvedEntityId =
      entityType === "FAIR" ? resolvedFuarId : target?.id || null;

    // Validasyon
    if (!resolvedFuarId) {
      setSubmitError(t("esnekForm.lutfenBirFuarSecin"));
      return;
    }
    if (!resolvedEntityId) {
      setSubmitError(t("esnekForm.lutfenHedefVarligiSecinGirin"));
      return;
    }

    // Zorunlu alan kontrolü (PHOTO hariç)
    const missing = allQuestions
      .filter((q) => q.required && q.answerType !== "PHOTO")
      .filter((q) => {
        const val = answers[q._id || q.id];
        return (
          val === undefined || val === null || val === "" ||
          (Array.isArray(val) && val.length === 0)
        );
      })
      .map((q) => q.name);

    if (missing.length > 0) {
      setSubmitError(
        t("esnekForm.zorunluAlanlarEksik", { v: missing.slice(0, 3).join(", "), v2: missing.length > 3 ? " …" : "" })
      );
      return;
    }

    // Cevap listesi oluştur (PHOTO tipler submission body'sine dahil edilmez)
    const answerList = allQuestions
      .filter((q) => q.answerType !== "PHOTO")
      .map((q) => {
        const qId  = q._id || q.id;
        const val  = answers[qId];
        const base = { questionNodeId: qId, answerType: q.answerType };

        if (q.answerType === "TEXT")
          return { ...base, textValue: val ?? "" };
        if (q.answerType === "NUMBER")
          return { ...base, numberValue: val !== "" && val != null ? parseFloat(val) : null };
        if (q.answerType === "SINGLE_CHOICE")
          return { ...base, selectedChoices: val ? [val] : [] };
        if (q.answerType === "MULTI_CHOICE")
          return { ...base, selectedChoices: Array.isArray(val) ? val : [] };
        return base;
      });

    setSubmitting(true);
    try {
      const body = {
        fuarId: resolvedFuarId,
        targetEntityType: entityType,
        targetEntityId: resolvedEntityId,
        answers: answerList,
      };
      const res = await submitForm(templateId, body);
      // Inline seçilen fotoğrafları otomatik yükle
      const submissionId = res?.data || res?.id || res?.submissionId;
      if (submissionId && Object.keys(photoFiles).length > 0) {
        await Promise.allSettled(
          Object.entries(photoFiles).map(([qId, file]) =>
            uploadFreeFormPhoto(submissionId, file, qId)
          )
        );
      }
      setSubmitted(res);
    } catch (err) {
      setSubmitError(
        err?.response?.data?.message || t("esnekForm.formGonderilemediLutfenTekrarDeneyin")
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render: Yükleniyor ──────────────────────────────────────────────────

  if (loadingTemplate) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="text-muted mt-2">{t("esnekForm.sablonYukleniyor")}</p>
          </div>
        </Container>
      </div>
    );
  }

  if (templateError) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("esnekForm.esnekFormDoldur")} pageTitle={t("esnekForm.fdsSaha")} />
          <Alert color="danger">{templateError}</Alert>
          <Button color="light" onClick={() => navigate(-1)}>
            <ChevronLeft size={14} className="me-1" />{t("common:geri")}
          </Button>
        </Container>
      </div>
    );
  }

  // ─── Render: Başarı ──────────────────────────────────────────────────────

  if (submitted) {
    const hasPhotos      = allQuestions.some((q) => q.answerType === "PHOTO");
    const submissionId   = submitted?.data || submitted?.id || submitted?.submissionId;

    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <CheckCircle2 size={56} className="text-success mb-3" />
            <h4 className="fw-bold">{t("esnekForm.formBasariylaGonderildi")}</h4>
            {submissionId && (
              <p className="text-muted small">{t("esnekForm.submissionId")}{" "}<strong>{submissionId}</strong></p>
            )}

            <div className="d-flex justify-content-center gap-2 mt-3 flex-wrap">
              {hasPhotos && submissionId && (
                <Button
                  color="primary"
                  onClick={() =>
                    navigate(`/FdsSaha/FreeForm/${templateId}/photos/${submissionId}`)
                  }
                >
                  <Camera size={14} className="me-1" />
                  {t("esnekForm.fotografYukle")}
                </Button>
              )}
              <Button
                color="soft-success"
                onClick={() => {
                  setSubmitted(null);
                  setAnswers({});
                  setTarget(null);
                }}
              >
                {t("esnekForm.yeniFormDoldur")}
              </Button>
            </div>

            <div className="mt-4">
              <button
                className="btn btn-link text-muted small"
                onClick={() => navigate(-1)}
              >
                <ChevronLeft size={13} className="me-1" />
                {t("esnekForm.geriDon")}
              </button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  // ─── Render: Form ────────────────────────────────────────────────────────

  const entityType = template?.targetEntityType;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("esnekForm.esnekFormDoldur")} pageTitle={t("esnekForm.fdsSaha")} />

        {/* Şablon Başlığı */}
        <Card className="border-0 shadow-sm mb-3">
          <CardBody className="py-3">
            <div className="d-flex align-items-start gap-3">
              <div className="flex-grow-1">
                <h5 className="fw-bold mb-1">{template?.name}</h5>
                {template?.description && (
                  <p className="text-muted small mb-0">{template.description}</p>
                )}
              </div>
              <Badge color={ENTITY_COLORS[entityType] || "secondary"}>
                {ENTITY_LABELS[entityType] || entityType}
              </Badge>
            </div>
          </CardBody>
        </Card>

        {/* ── Bağlam Kartı ───────────────────────────────────────────────── */}
        <Card className="border-0 shadow-sm mb-3">
          <CardBody>
            <h6 className="fw-semibold mb-3 text-muted small text-uppercase">
              {t("esnekForm.doldurmaBaglami")}
            </h6>

            {/* FAIR dışı tipler: fuarId seç */}
            {entityType !== "FAIR" && (
              <div className="mb-3">
                <Label className="form-label small fw-semibold mb-1">{t("esnekForm.fuar")}</Label>
                {fuarList.length > 0 ? (
                  <Input
                    type="select"
                    bsSize="sm"
                    value={fuarId}
                    onChange={(e) => {
                      setFuarId(e.target.value);
                      setTarget(null);
                    }}
                  >
                    <option value="">{t("esnekForm.fuarSecin")}</option>
                    {fuarList.map((f) => (
                      <option key={f.fuarId} value={f.fuarId}>
                        {f.fuarName || t("esnekForm.fuar2", { fuarId: f.fuarId })}
                      </option>
                    ))}
                  </Input>
                ) : (
                  <Input
                    type="number"
                    bsSize="sm"
                    value={fuarId}
                    placeholder={t("esnekForm.fuarIdGirin")}
                    onChange={(e) => {
                      setFuarId(e.target.value);
                      setTarget(null);
                    }}
                  />
                )}
              </div>
            )}

            {/* FAIR tipi: fuar = hedef varlık */}
            {entityType === "FAIR" && (
              <div className="mb-3">
                <Label className="form-label small fw-semibold mb-1">{t("esnekForm.fuarHedef")}</Label>
                {fuarList.length > 0 ? (
                  <Input
                    type="select"
                    bsSize="sm"
                    value={target?.id || ""}
                    onChange={(e) => {
                      const f = fuarList.find((x) => String(x.fuarId) === e.target.value);
                      setTarget(f ? { id: f.fuarId, label: f.fuarName || t("esnekForm.fuar2", { fuarId: f.fuarId }) } : null);
                    }}
                  >
                    <option value="">{t("esnekForm.fuarSecin")}</option>
                    {fuarList.map((f) => (
                      <option key={f.fuarId} value={f.fuarId}>
                        {f.fuarName || t("esnekForm.fuar2", { fuarId: f.fuarId })}
                      </option>
                    ))}
                  </Input>
                ) : (
                  <Input
                    type="number"
                    bsSize="sm"
                    value={target?.id || ""}
                    placeholder={t("esnekForm.fuarIdGirin")}
                    onChange={(e) =>
                      setTarget(
                        e.target.value
                          ? { id: parseInt(e.target.value), label: t("esnekForm.fuar3", { value: e.target.value }) }
                          : null
                      )
                    }
                  />
                )}
              </div>
            )}

            {/* STAND: arama + seçim */}
            {entityType === "STAND" && (
              <div className="mb-1">
                <Label className="form-label small fw-semibold mb-1">{t("esnekForm.stant")}</Label>
                {target ? (
                  <div className="d-flex align-items-center gap-2">
                    <Badge color="success" className="fs-6">{target.label}</Badge>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-muted small"
                      onClick={() => setTarget(null)}
                    >
                      {t("esnekForm.degistir")}
                    </button>
                  </div>
                ) : fuarId ? (
                  <StandSelector
                    fuarId={parseInt(fuarId)}
                    value={target}
                    onChange={setTarget}
                  />
                ) : (
                  <p className="text-muted small">{t("esnekForm.onceFuarSecin")}</p>
                )}
              </div>
            )}

            {/* SALON: sayısal ID girişi */}
            {entityType === "SALON" && fuarId && (
              <div className="mb-1">
                <Label className="form-label small fw-semibold mb-1">{t("esnekForm.salonId")}</Label>
                <Input
                  type="number"
                  bsSize="sm"
                  value={target?.id || ""}
                  placeholder={t("esnekForm.salonIdGirin")}
                  onChange={(e) =>
                    setTarget(
                      e.target.value
                        ? { id: parseInt(e.target.value), label: t("esnekForm.salon2", { value: e.target.value }) }
                        : null
                    )
                  }
                />
              </div>
            )}

            {/* PERSON: sayısal ID girişi */}
            {entityType === "PERSON" && fuarId && (
              <div className="mb-1">
                <Label className="form-label small fw-semibold mb-1">{t("esnekForm.kisiId")}</Label>
                <Input
                  type="number"
                  bsSize="sm"
                  value={target?.id || ""}
                  placeholder={t("esnekForm.kisiIdGirin")}
                  onChange={(e) =>
                    setTarget(
                      e.target.value
                        ? { id: parseInt(e.target.value), label: t("esnekForm.kisi2", { value: e.target.value }) }
                        : null
                    )
                  }
                />
              </div>
            )}
          </CardBody>
        </Card>

        {/* ── Sorular ────────────────────────────────────────────────────── */}

        {rootCategories.length === 0 && (
          <Card className="border-0 shadow-sm mb-3">
            <CardBody className="text-center py-5">
              <FolderOpen size={40} className="text-muted mb-2" />
              <p className="text-muted small mb-0">{t("esnekForm.buSablondaHenuzSoruBulunmuyor")}</p>
            </CardBody>
          </Card>
        )}

        {rootCategories.map((cat) => {
          const catId = cat._id || cat.id;
          return (
            <Card key={catId} className="border-0 shadow-sm mb-3">
              <CardBody className="p-0">
                {/* Kök kategori başlığı */}
                <div className="d-flex align-items-center gap-2 p-3 bg-light rounded-top border-bottom">
                  <FolderOpen size={15} className="text-primary flex-shrink-0" />
                  <span className="fw-semibold small flex-grow-1">{cat.name}</span>
                </div>
                {/* Recursive içerik */}
                {renderCategoryChildren(catId)}
              </CardBody>
            </Card>
          );
        })}

        {/* ── Gönder ─────────────────────────────────────────────────────── */}
        {submitError && (
          <Alert color="danger" className="mt-2" toggle={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        <div className="d-flex gap-2 mb-4">
          <Button color="light" onClick={() => navigate(-1)} disabled={submitting}>
            <ChevronLeft size={14} className="me-1" />
            {t("common:geri")}
          </Button>
          <Button
            color="primary"
            className="flex-grow-1"
            onClick={handleSubmit}
            disabled={submitting || rootCategories.length === 0}
          >
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t("esnekForm.gonderiliyor")}
              </>
            ) : (
              <>
                <Send size={14} className="me-1" />
                {t("esnekForm.formuGonder")}
              </>
            )}
          </Button>
        </div>
      </Container>
    </div>
  );
}




